import { DEFAULT_IMG_TO_BLOB_OPTIONS, IImgToBlobOptions } from '.';

export class ImageLoadError extends Error {
  constructor() {
    super('Failed to load image');
  }
}

/**
 * Loads a Blob into an HTMLImageElement and resolves once the image is ready.
 * Revokes the object URL after loading or on error.
 * @param blob - The image blob to load
 * @returns A fully loaded HTMLImageElement
 */
function loadImage(blob: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(blob);

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(img);
    };

    img.onerror = (error) => {
      URL.revokeObjectURL(objectUrl);
      reject(new ImageLoadError());
    };

    img.src = objectUrl;
  });
}

/**
 * Calculates the target width and height while maintaining aspect ratio.
 * Downscales only — images smaller than maxSize are left at their original size.
 * @param width - Original image width in pixels
 * @param height - Original image height in pixels
 * @param maxSize - Maximum allowed size for the longest side in pixels
 * @returns The scaled width and height in pixels
 */
function calcTargetDimensions(width: number, height: number, maxSize: number): { width: number; height: number } {
  const scaleFactor = Math.min(maxSize / width, maxSize / height);
  const effectiveScale = scaleFactor < 1 ? scaleFactor : 1;
  return {
    width: Math.round(width * effectiveScale),
    height: Math.round(height * effectiveScale)
  };
}

/**
 * Draws an image onto a new canvas at the given dimensions.
 * Fills the background with transparencyColor for JPEG output to handle transparency.
 * @param img - The source image element
 * @param targetWidth - Target canvas width in pixels
 * @param targetHeight - Target canvas height in pixels
 * @param mimeType - Output MIME type (affects background fill for JPEG)
 * @param transparencyColor - Background fill color used when mimeType is image/jpeg
 * @returns A canvas element with the image drawn at the target dimensions
 */
function drawToCanvas(
  img: HTMLImageElement,
  targetWidth: number,
  targetHeight: number,
  mimeType: IImgToBlobOptions['mimeType'],
  transparencyColor: string
): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = targetWidth;
  canvas.height = targetHeight;

  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas context not found');

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  if (mimeType === 'image/jpeg') {
    ctx.fillStyle = transparencyColor;
    ctx.fillRect(0, 0, targetWidth, targetHeight);
  }

  ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

  return canvas;
}

/**
 * Exports a canvas as a compressed Blob.
 * @param canvas - The canvas to export
 * @param mimeType - Output MIME type
 * @param quality - Compression quality between 0 and 1
 * @returns A compressed Blob in the specified MIME type
 */
function canvasToBlob(
  canvas: HTMLCanvasElement,
  mimeType: IImgToBlobOptions['mimeType'],
  quality: number
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error('canvas.toBlob returned no data'));
      },
      mimeType,
      quality
    );
  });
}

/**
 * Resizes and compresses an image using the native Canvas API.
 * Automatically converts HEIC/HEIF files before processing.
 * Maintains aspect ratio based on the longest side (maxSize).
 * @param file - The source image file or blob
 * @param optionsParams - Optional resize and compression settings
 * @returns A resized and compressed Blob in the specified MIME type
 */
export async function resizeToBlob(
  file: File | Blob,
  optionsParams: IImgToBlobOptions = DEFAULT_IMG_TO_BLOB_OPTIONS
): Promise<Blob> {
  const { maxSize, qualityFactor, transparencyColor, mimeType } = {
    ...DEFAULT_IMG_TO_BLOB_OPTIONS,
    ...optionsParams
  };

  // const sourceBlob = await normalizeImage(file, mimeType);
  const img = await loadImage(file);
  const { width, height } = calcTargetDimensions(img.width, img.height, maxSize!);
  const canvas = drawToCanvas(img, width, height, mimeType, transparencyColor!);
  const blob = await canvasToBlob(canvas, mimeType, qualityFactor!);

  return blob;
}

/**
 * Converts a Blob to a raw Base64 string.
 * Strips the data URL prefix (e.g. "data:image/jpeg;base64,").
 * @param blob - The blob to convert
 * @returns The raw Base64 encoded string without the data URL prefix
 */
export function blobToRawBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      const result = reader.result as string;
      if (result && result.includes(',')) {
        resolve(result.split(',')[1]);
      } else {
        reject(new Error('Failed to parse Base64 string'));
      }
    };

    reader.onerror = (error) => reject(error);

    reader.readAsDataURL(blob);
  });
}
